import { useState } from 'react';
import { Alert, Button, IconButton, InputAdornment, Link, Stack, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { HttpError, errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password, needTotp ? totp : undefined);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from !== '/login' ? from : '/', { replace: true });
    } catch (err) {
      if (err instanceof HttpError && err.code === 'MFA_REQUIRED') {
        setNeedTotp(true);
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={needTotp ? 'Two-factor authentication' : 'Sign in'} subtitle={needTotp ? 'Enter the 6-digit code from your authenticator app.' : 'Enter your email and password to sign in.'}>
      <form onSubmit={submit} noValidate>
        <Stack gap={3}>
          {error && <Alert severity="error">{error}</Alert>}
          {!needTotp ? (
            <>
              <TextField
                label="Email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                fullWidth
                size="medium"
                placeholder="name@company.com"
              />
              <TextField
                label="Password"
                type={reveal ? 'text' : 'password'}
                placeholder="Enter your password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                size="medium"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {/* Not labelled "password" so it never matches the field's own label. */}
                        <IconButton edge="end" size="small" sx={{ color: 'text.secondary' }} onClick={() => setReveal((r) => !r)} aria-label={reveal ? 'Hide characters' : 'Reveal characters'} aria-pressed={reveal}>
                          {reveal ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </>
          ) : (
            <TextField
              label="Authentication code"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              autoComplete="one-time-code"
              slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6 } }}
              fullWidth
              size="medium"
            />
          )}
          {!needTotp && (
            <Link component={RouterLink} to="/forgot-password" underline="none" fontSize={14} sx={{ alignSelf: 'flex-end', mt: -1, color: 'primary.main', '&:hover': { color: 'primary.dark' } }}>
              Forgot your password?
            </Link>
          )}
          <Button type="submit" variant="contained" size="large" disabled={busy || !email || !password || (needTotp && totp.length !== 6)}>
            {busy ? 'Signing in…' : needTotp ? 'Verify' : 'Sign in'}
          </Button>
          {needTotp && (
            <Link component="button" type="button" onClick={() => setNeedTotp(false)} underline="hover" fontSize={14}>
              Back
            </Link>
          )}
        </Stack>
      </form>
    </AuthLayout>
  );
}
